// src/views/MaterialsView.js
// ============================================================================
// VISTA: MATERIALES (unificada) + RETALES — PLAN-INVENTARIO-RETALES §6b
//
// Antes: 3 vistas del mismo `materials` (Catálogo · Inventario · …). Ahora:
//  • Pestaña "Materiales": GRID de tarjetas con identidad + CHIP DE STOCK
//    juntos. Acciones contextuales por permiso:
//      - Obrero (canManageInventory): "+ Inventariar" / "+ Retal" / historial.
//        Escriben SOLO vía Cloud Functions (addStockEntry / createRemnant).
//      - Admin (canManageMaterials): "Nuevo material" / editar / borrar.
//        El obrero NUNCA ve el formulario de definición (§6b).
//  • Pestaña "Retales": lista con filtros, DXF, etiqueta, marcar usado.
//  • "Mover paleta" (license plate §4b) reubica todo lo de la paleta de golpe.
// ============================================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase/firebase-config';
import MaterialModal from '../components/MaterialModal';
import TransactionsView from './TransactionsView';
import StockEntrySheet from '../components/Inventory/StockEntrySheet';
import RemnantSheet from '../components/Inventory/RemnantSheet';
import MovePalletModal from '../components/Inventory/MovePalletModal';
import RemnantsTab from '../components/Inventory/RemnantsTab';
import { t } from '../lib/i18n';
import { CATALOG_MIGRATION_LOCK } from '../lib/catalogConfig';
import { loadLocationConfig, locationLabel } from '../lib/locations';
import { buildMaterialLabelPdf } from '../lib/materialLabel';

// --- Iconos ---
const IconPlus = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"></path></svg>;
const IconPencil = () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"></path></svg>;
const IconTrash = () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>;
const IconHistory = () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path></svg>;
const IconPrinter = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd"></path><circle cx="15" cy="10" r="1"></circle></svg>;

const MaterialsView = ({ db, user, storage }) => {
    const [materials, setMaterials] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState({ search: '', category: 'all', color: 'all', thickness: 'all', stock: 'all' });
    const [activeTab, setActiveTab] = useState('materiales');
    const [locCfg, setLocCfg] = useState({ warehouses: [], racks: [], zones: [], pallets: [] });

    // Modales / hojas de acción
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [viewingTransactionsFor, setViewingTransactionsFor] = useState(null);
    const [stockSheetFor, setStockSheetFor] = useState(null);        // material → "+ Inventariar"
    const [remnantSheetFor, setRemnantSheetFor] = useState(null);    // { material|null } → "+ Retal"
    const [isMovePalletOpen, setIsMovePalletOpen] = useState(false);
    const [remnantsRefresh, setRemnantsRefresh] = useState(0); // señal: recargar lista de retales
    const [viewMode, setViewMode] = useState('grid');          // grid (piso) | list (todo de un golpe)
    const [lightbox, setLightbox] = useState(null);            // { url, name } → preview grande

    const canManageMaterials = user.permissions?.canManageMaterials;
    const canManageInventory = user.permissions?.canManageInventory;
    // Candado anti-regresión (Slice 5): durante la migración del catálogo, el alta/
    // edición/borrado de material queda deshabilitado (el conteo de stock sigue).
    const canEditCatalog = canManageMaterials && !CATALOG_MIGRATION_LOCK;

    const fetchMaterials = useCallback(async () => {
        setIsLoading(true);
        try {
            const materialsSnapshot = await getDocs(collection(db, "materials"));
            const materialsData = materialsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const nameOf = (m) => m.displayName || m.name || '';
            setMaterials(materialsData.sort((a, b) => nameOf(a).localeCompare(nameOf(b))));
        } catch (error) {
            console.error("Error cargando materiales:", error);
        } finally {
            setIsLoading(false);
        }
    }, [db]);

    const fetchLocations = useCallback(async () => {
        try {
            setLocCfg(await loadLocationConfig(db));
        } catch (e) {
            console.error("Error cargando ubicaciones:", e);
        }
    }, [db]);

    useEffect(() => { fetchMaterials(); fetchLocations(); }, [fetchMaterials, fetchLocations]);

    // Color a mostrar (FIX MAT-00050): el modelo nuevo lo guarda en attributes.color; los docs
    // viejos en el campo top-level m.color. DEBE declararse ANTES de uniqueValues (que lo usa en
    // el render); si queda debajo, el useMemo lo referencia en temporal dead zone → pantalla en blanco.
    const materialColor = (m) => (m.attributes && m.attributes.color) || m.color || '';

    const uniqueValues = useMemo(() => {
        const categories = new Set(); const colors = new Set(); const thicknesses = new Set();
        materials.forEach(m => {
            if (m.category) categories.add(m.category);
            const col = materialColor(m);
            if (col) colors.add(col);
            if (m.thickness?.value) thicknesses.add(m.thickness.value);
        });
        return {
            categories: [...categories].sort(),
            colors: [...colors].sort(),
            thicknesses: [...thicknesses].sort((a, b) => a - b),
        };
    }, [materials]);

    const getStockStatus = (material) => {
        if (!material.stock) return { key: 'na', text: t('materials.stock.na'), color: 'bg-gray-200 text-gray-800' };
        const { quantity, minStockLevel } = material.stock;
        if (quantity <= 0) return { key: 'out', text: t('materials.stock.out'), color: 'bg-red-200 text-red-800' };
        if (quantity <= minStockLevel) return { key: 'low', text: t('materials.stock.low'), color: 'bg-yellow-200 text-yellow-800' };
        return { key: 'ok', text: t('materials.stock.ok'), color: 'bg-green-200 text-green-800' };
    };

    // Color de texto del NÚMERO grande de stock (verde OK / amarillo bajo / rojo faltante).
    const stockTextColor = (key) => ({ ok: 'text-green-600', low: 'text-yellow-600', out: 'text-red-600' }[key] || 'text-gray-400');
    // Código canónico (MAT-NNNNN asignado por el sistema) con fallback al legacy derivado.
    const materialCode = (m) => m.code || m.uniqueCode || '';
    // Nombre legible (Slice 4: displayName generado; fallback al name viejo).
    const materialName = (m) => m.displayName || m.name || '';
    // Categoría legible (familia nueva → t('matCat.X'); legacy → tal cual).
    const catLabel = (c) => (c && t(`matCat.${c}`) !== `matCat.${c}` ? t(`matCat.${c}`) : (c || ''));
    const stockUnit = (m) => (m.presentation === 'Plancha' ? t('materials.unit.sheets')
        : m.presentation === 'Rollo' ? t('materials.unit.rolls') : t('materials.unit.units'));
    const openImage = (m) => { if (m.imageUrl) setLightbox({ url: m.imageUrl, name: m.name }); };

    const filteredMaterials = useMemo(() => {
        return materials.filter(m => {
            // Slice 4: oculta los inactivos (soft-delete / fusionados). active:false → fuera.
            if (m.active === false) return false;
            const searchLower = filters.search.toLowerCase();
            const matchesSearch = (m.displayName || '').toLowerCase().includes(searchLower)
                || (m.name || '').toLowerCase().includes(searchLower)
                || (m.specCode || '').toLowerCase().includes(searchLower)
                || (m.code || '').toLowerCase().includes(searchLower)
                || (m.uniqueCode || '').toLowerCase().includes(searchLower)
                || (m.legacyCode || '').toLowerCase().includes(searchLower);
            const matchesCategory = filters.category === 'all' || m.category === filters.category;
            const matchesColor = filters.color === 'all' || materialColor(m) === filters.color;
            const matchesThickness = filters.thickness === 'all' || m.thickness?.value?.toString() === filters.thickness;
            const matchesStock = filters.stock === 'all' || getStockStatus(m).key === filters.stock;
            return matchesSearch && matchesCategory && matchesColor && matchesThickness && matchesStock;
        });
    }, [materials, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // ── Definición de material (admin, §6b) — write 100% server-side (Slice 4) ──
    const handleOpenMaterialModal = (material = null) => {
        setSelectedMaterial(material);
        setIsMaterialModalOpen(true);
    };
    const closeMaterialModal = () => { setIsMaterialModalOpen(false); setSelectedMaterial(null); };

    // Alta/edición vía CF `upsertMaterial`. Sube la imagen y devuelve el resultado al
    // modal (incl. la respuesta de dedup, para que ofrezca "sumar stock al existente").
    const handleSaveMaterial = async (payload, imageFile) => {
        let imageUrl = payload.imageUrl || '';
        if (imageFile) {
            const storageRef = ref(storage, `materials/${Date.now()}_${imageFile.name}`);
            await uploadBytes(storageRef, imageFile);
            imageUrl = await getDownloadURL(storageRef);
        }
        const fn = httpsCallable(functions, 'upsertMaterial');
        const res = await fn({ ...payload, imageUrl });
        const data = res.data || {};
        if (!data.duplicate) { await fetchMaterials(); }
        return data;
    };

    // "Sumar stock al existente" desde el banner de dedup del modal → abre +Inventariar.
    const handleAddStockToExisting = (existing) => {
        const full = materials.find((m) => m.id === existing.materialId);
        closeMaterialModal();
        if (full) setStockSheetFor(full);
    };

    // Soft-delete (D-S1) vía CF `deactivateMaterial`: no hard-delete + guard de referencias.
    const handleDeleteMaterial = async (materialId) => {
        if (!window.confirm(t('materials.deactivateConfirm'))) return;
        try {
            const fn = httpsCallable(functions, 'deactivateMaterial');
            await fn({ id: materialId });
            await fetchMaterials();
        } catch (e) {
            console.error('deactivateMaterial falló:', e);
            const blocked = e.code === 'functions/failed-precondition' || (e.message || '').toLowerCase().includes('retal');
            alert(blocked ? `${t('materials.deactivateBlocked')}${e.message}` : (e.message || t('materials.deactivateError')));
        }
    };

    // ── "No está en la lista" (§6b): avisa al admin, NO deja crear al obrero ──
    const handleNotInList = async () => {
        const desc = window.prompt(t('materials.notInList.prompt'));
        if (!desc || !desc.trim()) return;
        try {
            const adminsSnap = await getDocs(query(collection(db, 'users'), where('roles', 'array-contains', 'Admin')));
            const to = adminsSnap.docs.map(d => d.data().email).filter(Boolean);
            if (!to.length) throw new Error('No hay administradores con email.');
            await addDoc(collection(db, 'mail'), {
                to,
                message: {
                    subject: 'Material faltante en el catálogo (reportado desde Materiales)',
                    text: `${user.displayName || user.email || 'Un usuario'} reporta que falta este material en el catálogo:\n\n${desc.trim()}\n\nRegistrarlo desde "Nuevo Material" (requiere canManageMaterials).`,
                },
            });
            alert(t('materials.notInList.sent'));
        } catch (e) {
            console.error('Aviso de material faltante falló:', e);
            alert(t('materials.notInList.error'));
        }
    };

    const formatThickness = (m) => (m.thickness?.value ? `${m.thickness.value} ${m.thickness.unit || 'mm'}` : null);
    const formatDimensions = (m) => (m.dimensions?.width && m.dimensions?.height ? `${m.dimensions.width}×${m.dimensions.height} ${m.dimensions.unit || ''}` : null);
    const stockLocationText = (m) => (m.stock?.locationRef ? locationLabel(m.stock.locationRef, locCfg) : (m.stock?.location || ''));

    // Etiqueta del material (espejo de la de retal): código + QR + specs + ubicación.
    const printMaterialLabel = (m) => buildMaterialLabelPdf({ material: m, locationText: stockLocationText(m) });

    return (
        <div className="p-4 md:p-6 h-full flex flex-col">
            {viewingTransactionsFor && (
                <TransactionsView
                    material={viewingTransactionsFor}
                    db={db}
                    user={user}
                    onClose={() => { setViewingTransactionsFor(null); fetchMaterials(); }}
                />
            )}
            {canManageMaterials && <MaterialModal isOpen={isMaterialModalOpen} onClose={closeMaterialModal} onSave={handleSaveMaterial} materialData={selectedMaterial} onAddStockToExisting={handleAddStockToExisting} />}
            {stockSheetFor && (
                <StockEntrySheet material={stockSheetFor} cfg={locCfg}
                    onClose={() => setStockSheetFor(null)} onSaved={fetchMaterials} />
            )}
            {remnantSheetFor && (
                <RemnantSheet material={remnantSheetFor.material} materials={materials} cfg={locCfg}
                    onClose={() => setRemnantSheetFor(null)} onSaved={() => setRemnantsRefresh(n => n + 1)} />
            )}
            {isMovePalletOpen && (
                <MovePalletModal cfg={locCfg} onClose={() => setIsMovePalletOpen(false)} onMoved={fetchLocations} />
            )}
            {/* #3 Preview grande del material (clic en la foto) */}
            {lightbox && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => setLightbox(null)}>
                    <div className="max-w-3xl max-h-full text-center" onClick={(e) => e.stopPropagation()}>
                        <img src={lightbox.url} alt={lightbox.name} className="max-w-full max-h-[80vh] rounded-lg shadow-2xl mx-auto" />
                        <p className="text-white mt-3 font-semibold">{lightbox.name}</p>
                        <button onClick={() => setLightbox(null)} className="mt-2 px-4 py-2 text-sm font-medium text-gray-800 bg-white rounded-lg hover:bg-gray-200">{t('remnant.close')}</button>
                    </div>
                </div>
            )}

            <div className="flex-shrink-0">
                {CATALOG_MIGRATION_LOCK && canManageMaterials && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-sm flex items-center gap-2">
                        <span aria-hidden="true">🔒</span><span>{t('materials.migrationLock.banner')}</span>
                    </div>
                )}
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
                    <h1 className="text-3xl font-bold text-gray-800">{t('materials.title')}</h1>
                    <div className="flex gap-2 w-full md:w-auto">
                        {canManageInventory && (
                            <button onClick={() => setIsMovePalletOpen(true)} className="flex-1 md:flex-none px-4 py-2 font-bold text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100">
                                🚚 {t('materials.movePallet')}
                            </button>
                        )}
                        {canEditCatalog && (
                            <button onClick={() => handleOpenMaterialModal()} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                                <IconPlus /> {t('materials.new')}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
                    <input type="text" name="search" placeholder={t('materials.search')} className="w-full p-2 border border-gray-300 rounded-md" value={filters.search} onChange={handleFilterChange} />
                    <select name="category" className="w-full p-2 border border-gray-300 rounded-md" value={filters.category} onChange={handleFilterChange}>
                        <option value="all">{t('materials.allCategories')}</option>
                        {uniqueValues.categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    <select name="color" className="w-full p-2 border border-gray-300 rounded-md" value={filters.color} onChange={handleFilterChange}>
                        <option value="all">{t('materials.allColors')}</option>
                        {uniqueValues.colors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select name="thickness" className="w-full p-2 border border-gray-300 rounded-md" value={filters.thickness} onChange={handleFilterChange}>
                        <option value="all">{t('materials.allThicknesses')}</option>
                        {uniqueValues.thicknesses.map(th => <option key={th} value={th}>{th} mm</option>)}
                    </select>
                    <select name="stock" className="w-full p-2 border border-gray-300 rounded-md" value={filters.stock} onChange={handleFilterChange}>
                        <option value="all">{t('materials.allStock')}</option>
                        <option value="ok">{t('materials.stock.ok')}</option>
                        <option value="low">{t('materials.stock.low')}</option>
                        <option value="out">{t('materials.stock.out')}</option>
                    </select>
                </div>

                <div className="border-b border-gray-200 flex items-center justify-between">
                    <nav className="flex space-x-4" aria-label="Tabs">
                        <button onClick={() => setActiveTab('materiales')} className={`px-3 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'materiales' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>{t('materials.tab.materials')}</button>
                        <button onClick={() => setActiveTab('retales')} className={`px-3 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'retales' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>{t('materials.tab.remnants')}</button>
                    </nav>
                    {/* #4 Toggle grid (piso) / lista (todo de un golpe) — solo en Materiales */}
                    {activeTab === 'materiales' && (
                        <div className="flex gap-1 pb-1">
                            <button onClick={() => setViewMode('grid')} title={t('materials.view.grid')}
                                className={`px-3 py-1.5 text-sm rounded-md border ${viewMode === 'grid' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>▦ {t('materials.view.grid')}</button>
                            <button onClick={() => setViewMode('list')} title={t('materials.view.list')}
                                className={`px-3 py-1.5 text-sm rounded-md border ${viewMode === 'list' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>☰ {t('materials.view.list')}</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-grow bg-white rounded-b-xl shadow-lg overflow-hidden">
                {activeTab === 'materiales' && (
                    isLoading ? <div className="text-center p-10">{t('materials.loading')}</div> : (
                        <div className="overflow-auto h-full p-4">
                            {filteredMaterials.length === 0 && (
                                <p className="text-center text-gray-500 py-8">{t('materials.empty')}</p>
                            )}
                            {viewMode === 'grid' ? (
                            /* Cuadrícula CONDENSADA: más columnas, tarjeta apretada, número de
                               stock grande al lado de la foto (no en bloque aparte). */
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                                {filteredMaterials.map(m => {
                                    const status = getStockStatus(m);
                                    const locText = stockLocationText(m);
                                    return (
                                        <div key={m.id} className="border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                                            <div className="flex gap-2 p-2">
                                                {/* #3 clic en la foto → lightbox */}
                                                <img src={m.imageUrl || 'https://placehold.co/100x100/eee/ccc?text=N/A'} alt={m.name}
                                                    onClick={() => openImage(m)}
                                                    className={`w-16 h-16 object-cover rounded-md border flex-shrink-0 ${m.imageUrl ? 'cursor-zoom-in hover:opacity-90' : ''}`} />
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-sm text-gray-900 leading-tight truncate" title={materialName(m)}>{materialName(m)}</p>
                                                    {m.specCode && <p className="text-[10px] font-mono text-gray-400 truncate" title={m.specCode}>{m.specCode}</p>}
                                                    <p className="text-[11px] text-gray-500 truncate">{catLabel(m.category)}{formatThickness(m) ? ` · ${formatThickness(m)}` : ''}{formatDimensions(m) ? ` · ${formatDimensions(m)}` : ''}</p>
                                                    {materialColor(m) && (
                                                        <p className="text-[11px] text-gray-500 flex items-center gap-1 truncate">
                                                            <span className="inline-block w-2.5 h-2.5 rounded-full border flex-shrink-0" style={{ backgroundColor: m.colorHex || '#ccc' }}></span>
                                                            {materialColor(m)}
                                                        </p>
                                                    )}
                                                    {locText && <p className="text-[10px] text-gray-400 truncate" title={locText}>📍 {locText}</p>}
                                                </div>
                                                {/* #1 NÚMERO de stock grande con color de estado, pegado a la derecha */}
                                                <div className="text-right flex-shrink-0">
                                                    <div className={`text-3xl font-extrabold leading-none ${stockTextColor(status.key)}`}>{m.stock?.quantity ?? '–'}</div>
                                                    <div className={`text-[10px] font-semibold ${stockTextColor(status.key)}`}>{stockUnit(m)} · {status.text}</div>
                                                </div>
                                            </div>
                                            <div className="mt-auto border-t bg-gray-50 px-2 py-1.5 space-y-1.5">
                                                {canManageInventory && (
                                                    <div className="flex gap-1.5">
                                                        <button onClick={() => setStockSheetFor(m)}
                                                            className="flex-1 px-1 py-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-300 rounded-md hover:bg-green-100">
                                                            {t('materials.addStock')}
                                                        </button>
                                                        <button onClick={() => setRemnantSheetFor({ material: m })}
                                                            className="flex-1 px-1 py-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-md hover:bg-amber-100">
                                                            {t('materials.addRemnant')}
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-1">
                                                    {(canManageInventory || canManageMaterials) && (
                                                        <button onClick={() => printMaterialLabel(m)}
                                                            className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100">
                                                            🏷 {t('materials.printLabel')}
                                                        </button>
                                                    )}
                                                    {canManageInventory && (
                                                        <button onClick={() => setViewingTransactionsFor(m)} title={t('materials.history')}
                                                            className="p-1 text-gray-500 hover:text-blue-600"><IconHistory /></button>
                                                    )}
                                                    {canEditCatalog && (
                                                        <>
                                                            <button onClick={() => handleOpenMaterialModal(m)} title={t('materials.edit')} className="p-1 text-gray-500 hover:text-blue-600"><IconPencil /></button>
                                                            <button onClick={() => handleDeleteMaterial(m.id)} title={t('materials.deactivate')} className="p-1 text-gray-500 hover:text-red-600"><IconTrash /></button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            ) : (
                            /* #4 VISTA LISTA: tabla con TODA la info de un golpe.
                               SIN wrapper overflow-x propio: el scroll vive en el contenedor
                               exterior → el thead sticky SÍ se queda fijo al hacer scroll. */
                            <div className="border rounded-lg">
                                <table className="w-full text-sm text-left text-gray-600 whitespace-nowrap">
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th className="px-3 py-2 text-center">{t('materials.col.photo')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.code')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.name')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.category')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.presentation')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.measures')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.thickness')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.color')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.stock')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.location')}</th>
                                            <th className="px-3 py-2 text-center">{t('materials.col.notes')}</th>
                                            <th className="px-3 py-2 text-center">{t('remnant.col.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y bg-white">
                                        {filteredMaterials.map(m => {
                                            const status = getStockStatus(m);
                                            return (
                                                <tr key={m.id} className="hover:bg-gray-50">
                                                    <td className="px-3 py-2"><img src={m.imageUrl || 'https://placehold.co/100x100/eee/ccc?text=N/A'} alt={m.name} onClick={() => openImage(m)} className={`w-10 h-10 object-cover rounded border ${m.imageUrl ? 'cursor-zoom-in' : ''}`} /></td>
                                                    <td className="px-3 py-2 font-mono text-xs" title={m.legacyCode ? `Legacy: ${m.legacyCode}` : undefined}>{materialCode(m) || '—'}</td>
                                                    <td className="px-3 py-2 font-medium text-gray-900">
                                                        <div>{materialName(m)}</div>
                                                        {m.specCode && <div className="font-mono text-[11px] text-gray-400">{m.specCode}</div>}
                                                    </td>
                                                    <td className="px-3 py-2">{catLabel(m.category)}</td>
                                                    <td className="px-3 py-2">{m.presentation || '—'}</td>
                                                    <td className="px-3 py-2">{formatDimensions(m) || '—'}</td>
                                                    <td className="px-3 py-2">{formatThickness(m) || '—'}</td>
                                                    <td className="px-3 py-2">{materialColor(m) ? (<span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full border" style={{ backgroundColor: m.colorHex || '#ccc' }}></span>{materialColor(m)}</span>) : '—'}</td>
                                                    <td className={`px-3 py-2 text-center font-bold ${stockTextColor(status.key)}`}>{m.stock?.quantity ?? '–'}</td>
                                                    <td className="px-3 py-2">{stockLocationText(m) || '—'}</td>
                                                    <td className="px-3 py-2 max-w-xs truncate" title={m.description}>{m.description || '—'}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center gap-1 justify-center">
                                                            {canManageInventory && (
                                                                <>
                                                                    <button onClick={() => setStockSheetFor(m)} title={t('materials.addStock')} className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-300 rounded hover:bg-green-100">+St</button>
                                                                    <button onClick={() => setRemnantSheetFor({ material: m })} title={t('materials.addRemnant')} className="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded hover:bg-amber-100">+Ret</button>
                                                                    <button onClick={() => setViewingTransactionsFor(m)} title={t('materials.history')} className="p-1 text-gray-500 hover:text-blue-600"><IconHistory /></button>
                                                                </>
                                                            )}
                                                            {(canManageInventory || canManageMaterials) && (
                                                                <button onClick={() => printMaterialLabel(m)} title={t('materials.printLabel')} aria-label={t('materials.printLabel')}
                                                                    className="inline-flex items-center justify-center min-w-[40px] min-h-[40px] p-2 text-gray-500 hover:text-gray-900 rounded-md hover:bg-gray-100"><IconPrinter /></button>
                                                            )}
                                                            {canEditCatalog && (
                                                                <>
                                                                    <button onClick={() => handleOpenMaterialModal(m)} title={t('materials.edit')} className="p-1 text-gray-500 hover:text-blue-600"><IconPencil /></button>
                                                                    <button onClick={() => handleDeleteMaterial(m.id)} title={t('materials.deactivate')} className="p-1 text-gray-500 hover:text-red-600"><IconTrash /></button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            )}
                            {/* Botón discreto: el obrero AVISA, no crea (§6b) */}
                            {canManageInventory && !canManageMaterials && (
                                <div className="text-center mt-6">
                                    <button onClick={handleNotInList} className="text-sm text-gray-500 underline hover:text-gray-700">
                                        {t('materials.notInList')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                )}

                {activeTab === 'retales' && (
                    <RemnantsTab db={db} cfg={locCfg} canManageInventory={canManageInventory}
                        refreshKey={remnantsRefresh}
                        onNewRemnant={() => setRemnantSheetFor({ material: null })} />
                )}
            </div>
        </div>
    );
};

export default MaterialsView;
